<template>
  <section class="groove-builders-workspace">
    <sequence-navigation v-if="feature === 'sequences'" />
    <builder-nav :feature="feature" />
    <p v-if="navigationError" class="groove-builders-error" role="alert">{{ navigationError }}</p>
    <iframe ref="frame" :src="frameUrl" :title="title" sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads" referrerpolicy="same-origin" class="groove-builders-frame" :style="feature === 'sequences' && frameHeight ? { height: frameHeight + 'px', minHeight: '0' } : null" />
  </section>
</template>
<script>
import axios from 'axios';
import BuilderNav from './BuilderNav.vue';
import SequenceNavigation from './SequenceNavigation.vue';
import assets from './assets.json';
import { builderFeatures, sourceRoute, grooveRoute, allowedRequest, isFrameMessage, responseBody, uploadFormData } from './contracts';
export default {
  name: 'BuilderWorkspace', middleware: 'auth', layout: 'defaultWithoutVuePerfectScrollbar',
  components: { BuilderNav, SequenceNavigation }, props: { feature: { type: String, required: true } },
  data() { return { frameHeight: null, frameResizeObserver: null, channel: Array.from(crypto.getRandomValues(new Uint32Array(4))).map(value => value.toString(16)).join(''), ready: false, navigationError: '', requests: new Map() }; },
  computed: {
    title() { return builderFeatures[this.feature].title; },
    frameUrl() { return (process.env.BASE_URL || '/') + assets.entry + '?channel=' + this.channel; }
  },
  watch: { '$route.fullPath'() { this.initialize(); } },
  mounted() {
    window.addEventListener('message', this.onMessage);
    if (this.feature === 'sequences') {
      window.addEventListener('resize', this.resizeSequenceFrame);
      this.$nextTick(() => {
        this.resizeSequenceFrame();
        if (typeof ResizeObserver !== 'undefined') {
          this.frameResizeObserver = new ResizeObserver(this.resizeSequenceFrame);
          this.$el.querySelectorAll('nav').forEach(nav => this.frameResizeObserver.observe(nav));
        }
      });
    }
  },
  beforeDestroy() { window.removeEventListener('resize', this.resizeSequenceFrame); if (this.frameResizeObserver) this.frameResizeObserver.disconnect(); window.removeEventListener('message', this.onMessage); this.requests.forEach(cancel => cancel('Builder closed')); this.requests.clear(); },
  methods: {
    resizeSequenceFrame() {
      const frame = this.$refs.frame;
      if (frame) this.frameHeight = Math.max(240, Math.floor(window.innerHeight - frame.getBoundingClientRect().top - 16));
    },
    post(message) { if (this.$refs.frame) this.$refs.frame.contentWindow.postMessage(Object.assign({ channel: this.channel }, message), window.location.origin); },
    initialize() { if (this.ready) { this.navigationError = ''; this.post({ type: 'groove:init', path: sourceRoute(this.feature, this.$route), base: this.$router.options.base }); } },
    async onMessage(event) {
      if (!isFrameMessage(event, this.$refs.frame, this.channel, window.location.origin)) return;
      const message = event.data;
      if (message.type === 'groove:ready') { this.ready = true; this.initialize(); return; }
      if (message.type === 'groove:navigate') {
        const destination = grooveRoute(message.path);
        if (destination) { if (destination === this.$route.fullPath) this.initialize(); else this.$router.push(destination); }
        else { this.navigationError = 'Open this resource from the GrooveMail navigation, then return to your builder.'; }
        return;
      }
      if (message.type !== 'groove:request' || typeof message.id !== 'string' || message.id.length > 80 || this.requests.has(message.id)) return;
      const method = String(message.method || 'GET').toUpperCase();
      const upload = message.upload ? uploadFormData(message) : null;
      if ((message.upload && !upload) || (!upload && message.path === '/api/storage/upload') || !allowedRequest(message.path, method) || (message.body && (typeof message.body !== 'string' || message.body.length > 3000000)) || this.requests.size >= 32) {
        this.post({ type: 'groove:response', id: message.id, status: 400, body: { error: { message: 'This builder request is not supported.' } } }); return;
      }
      const cancellation = axios.CancelToken.source();
      this.requests.set(message.id, cancellation.cancel);
      try {
        const response = await axios({ url: '/builders/' + message.path.slice('/api/'.length).replace(/^integrations\/composio/, 'composio'), method, data: upload || message.body || undefined, headers: upload ? {} : { 'Content-Type': 'application/json' }, timeout: 60000, cancelToken: cancellation.token });
        this.post({ type: 'groove:response', id: message.id, status: response.status, body: response.data });
      } catch (error) {
        if (!axios.isCancel(error)) this.post({ type: 'groove:response', id: message.id, status: (error.response && error.response.status) || 502, body: responseBody(error) });
      } finally { this.requests.delete(message.id); }
    }
  }
};
</script>
<style scoped>
.groove-builders-workspace { min-width:0; width:100%; background:#fff; }
.groove-builders-frame { display:block; width:100%; height:calc(100dvh - 142px); min-height:620px; border:0; background:#fff; }
.groove-builders-error {padding:12px 16px;color:#9a3412;background:#fff7ed;}
@media (max-width:600px) { .groove-builders-frame { height:calc(100dvh - 160px);min-height:580px; } }
</style>
